import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Loader2, Share2 } from "lucide-react";
import { http } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Invite() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (user === false) nav("/login");
    if (user && user.id) {
      http.get("/me/referral").then(({ data }) => setInfo(data)).catch(() => {});
    }
  }, [user, nav]);

  if (user === null || !info) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return null;

  const inviteUrl = `${window.location.origin}/wholesale?ref=${info.code}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(inviteUrl)}&size=500x500&margin=10&color=1A1A1A&bgcolor=FFFDF8`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success("Link copied!");
    } catch { toast.error("Copy failed"); }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Alekas Treats Wholesale",
          text: `Stock real Mexican chamoy candy — made with 100% Mexican spices. Apply for wholesale using my code ${info.code}:`,
          url: inviteUrl,
        });
      } catch (e) {
        // Web Share dismissed by user — silently OK
      }
    } else {
      copy();
    }
  };
  const printCard = () => window.print();

  return (
    <div className="bg-[#FFFDF8]" data-testid="invite-root">
      <style>{`
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
          .print-card { break-inside: avoid; box-shadow: none; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="no-print">
          <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Refer & earn credibility</div>
          <h1 className="font-heading text-5xl md:text-6xl uppercase tracking-wide">Your wholesale invite</h1>
          <p className="text-lg text-[#5C5A56] mt-4 max-w-2xl">Share your unique link at trade shows, in DMs, or print the card below for your booth. Every shop that applies through your code gets fast-tracked review — and we&apos;ll thank you back.</p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-10">
          <div className="no-print space-y-6">
            <div className="bg-[#FDF4E3] border border-[#E5D8C1] p-6">
              <div className="text-xs uppercase tracking-widest text-[#5C5A56]">Your code</div>
              <div className="font-heading text-6xl tracking-wider text-[#D32F2F] mt-2" data-testid="invite-code">{info.code}</div>
              <div className="text-xs uppercase tracking-widest text-[#5C5A56] mt-6">Shareable link</div>
              <div className="mt-2 bg-white border border-[#E5D8C1] px-3 py-2 text-sm break-all font-mono" data-testid="invite-url">{inviteUrl}</div>
              <div className="mt-4 flex flex-wrap gap-3">
                <button onClick={copy} className="bg-[#1A1A1A] text-white hover:bg-[#D32F2F] px-5 py-3 uppercase tracking-widest text-xs font-heading flex items-center gap-2" data-testid="invite-copy"><Copy size={14} /> Copy link</button>
                <button onClick={share} className="border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-5 py-3 uppercase tracking-widest text-xs font-heading flex items-center gap-2" data-testid="invite-share"><Share2 size={14} /> Share</button>
                <button onClick={printCard} className="border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-5 py-3 uppercase tracking-widest text-xs font-heading flex items-center gap-2" data-testid="invite-print"><Download size={14} /> Print booth card</button>
              </div>
            </div>

            <div className="bg-[#1A1A1A] text-[#FFFDF8] p-6">
              <div className="text-xs uppercase tracking-widest text-[#FFB300]">Invites converted</div>
              <div className="font-heading text-6xl mt-2">{info.referral_count}</div>
              <div className="text-xs text-white/60 mt-1 uppercase tracking-widest">Applications submitted with your code</div>
            </div>

            <div className="text-sm text-[#5C5A56] leading-relaxed">
              <b className="text-[#1A1A1A]">Pro tip:</b> Print the card at 4x6&quot; and clip it to your product display. When shop owners scan the QR code they land on a pre-filled application with your code attached — no typing, no friction.
            </div>
          </div>

          <div className="print-card bg-[#FFFDF8] border-2 border-[#1A1A1A] p-8 aspect-[3/4] flex flex-col justify-between relative overflow-hidden" data-testid="invite-card">
            <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#FFB300] rounded-full opacity-70" />
            <div className="absolute -bottom-20 -left-16 w-64 h-64 bg-[#D32F2F] rounded-full opacity-15" />
            <div className="relative z-10">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Alekas Treats" className="h-14 w-auto bg-white p-1" />
                <div>
                  <div className="font-heading text-2xl tracking-wider leading-none">ALEKAS TREATS</div>
                  <div className="text-[10px] tracking-[0.3em] text-[#5C5A56] uppercase">100% Mexican spices</div>
                </div>
              </div>
              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.3em] text-[#D32F2F]">Wholesale invite</div>
                <div className="font-heading text-4xl uppercase tracking-tight leading-[0.95] mt-2">Stock the chamoy candy that&apos;s viral on TikTok.</div>
              </div>
            </div>
            <div className="relative z-10 flex flex-col items-center">
              <img src={qrUrl} alt="Scan to apply" className="w-44 h-44 bg-white p-1" data-testid="invite-qr" />
              <div className="text-xs uppercase tracking-widest text-[#5C5A56] mt-2">Scan → Apply → Ship</div>
              <div className="mt-4 text-center">
                <div className="text-[10px] uppercase tracking-widest text-[#5C5A56]">Or enter code</div>
                <div className="font-heading text-3xl tracking-widest text-[#D32F2F]">{info.code}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
